import { useState } from "react";
import BulkPayoutLayout from "../../components/BulkPayoutLayout/BulkPayoutLayout";
import { useNavigate } from "react-router-dom";
import NotificationSwitch from "../../components/NotificationSwitch/NotificationSwitch";
import InitiateCard from "../../components/InitiateCard/InitiateCard";
import { useMutation } from "react-query";
import { downloadFile, downloadFileFromResponse } from "../../utils/downloadFile";
import { AxiosError } from "axios";
import { toast } from "react-toastify";
import {
  bulkPayoutFileUpload,
  downloadBulkPayoutTemplate,
} from "../../services/bulk-payout.service";

interface UploadedFileType {
  file: File;
  name: string;
  size: number;
  type: string;
  base64Format: string;
}

const InitiateBulkPayout = () => {
  const [formType, setFormType] = useState("Vendor");
  const [bulkPayeeFile, setBulkPayeeFile] = useState<UploadedFileType[]>([]);

  const navigate = useNavigate();
  const handleSwitch = (filter: string) => {
    setFormType(filter);
  };

  const handleManualEntryNavigation = () => {
    navigate("/bulk-payout-manual-entry", {
      state: { payeeType: formType }, // Pass payeeType as state
    });
  };

  const { mutate: mutateDownloadAsync } = useMutation(
    downloadBulkPayoutTemplate,
    {
      onSuccess: (response) => {
        // Check if it's an error response (you might need to adjust this based on your API)
        if (response.status === 200) {
          downloadFileFromResponse(
            response,
            `${formType} Bulk Payout Template`
          );
          toast.success(
            `${formType} bulk upload file template download successful`
          );
        } else {
          toast.error("Download failed");
        }
      },
      onError: async (error: AxiosError<any>) => {
        let errorMessage = "Something went wrong. Please try again.";

        try {
          if (error?.response?.data instanceof Blob) {
            // Convert blob to JSON
            const text = await error.response.data.text();
            const json = JSON.parse(text);

            if (json?.errorMessageList?.length) {
              errorMessage = json.errorMessageList.join(". ");
            } else if (json?.message) {
              errorMessage = json.message;
            }
          } else {
            // Handle regular JSON case
            const errorData = error?.response?.data;
            if (errorData?.errors) {
              const errorMessages: string[] = [];
              for (const field in errorData.errors) {
                if (Array.isArray(errorData.errors[field])) {
                  errorMessages.push(...errorData.errors[field]);
                }
              }
              if (errorMessages.length > 0) {
                errorMessage = errorMessages.join(". ");
              }
            } else if (errorData?.message) {
              errorMessage = errorData.message;
            }
          }
        } catch (parseErr) {
          console.error("Failed to parse error blob", parseErr);
        }

        toast.error(errorMessage);
      },
    }
  );

  const { mutate: uploadBulkPayoutFile, isLoading: isUploadingData } =
    useMutation(bulkPayoutFileUpload, {
      onSuccess: (response) => {
        if (
          (response?.errors &&
            response?.errors[0] ===
              "vat | whtgeneralcontract | whtconsultancyservice | stampduty header(s) not present in file uploaded.") ||
          response?.result?.dataItems?.length === 0 ||
          (response?.errors &&
            response?.errors[0] === "Invalid File Format supplied for Employee")
        ) {
          toast?.error(`Invalid CSV file uploaded!`);
        } else if (response?.errors && response?.result === null && response?.errors[0]) {
          toast?.error(response?.errors[0]);
        } else {
          navigate("/bulk-payout-review", {
            state: {
              bulkPayoutReviewData: response,
              payeeType: formType,
            },
          });
        }
      },
      onError: (error: AxiosError<{ message: string }>) => {
        if (error?.response) {
          toast.error(error?.response?.data?.message);
        }
      },
    });

  const handleUploadSupportingDocument = async () => {
    let base64File = bulkPayeeFile.map((doc) => doc.base64Format)[0] || "";
    const payeeType = formType === "Vendor" ? 1 : 2;
    const payload = {
      base64File: base64File,
      payeeType: payeeType,
    };
    uploadBulkPayoutFile(payload);
  };

  const downloadTemplate = async () => {
    toast.success(`${formType} bulk upload file template download initated`);
    const PayeeType = formType === "Vendor" ? 1 : 2;
    await mutateDownloadAsync({ PayeeType });
  };

  return (
    <BulkPayoutLayout
      pageHeading="Initiate Bulk Payment"
      topCancelButtonAction={() => navigate("/bulk-payout")}
      bottomCancelButtonAction={() => navigate("/bulk-payout")}
      continueButtonAction={() => {
        handleUploadSupportingDocument();
      }}
      isContinueLoading={isUploadingData}
      disableContinue={bulkPayeeFile.length === 0}>
      <div className="flex flex-col items-center mt-10">
        <p className="text-2xl font-semibold text-[#0D0D0D] w-[95%] md:w-[300px] text-center">
          Who are you initiating this bulk payout for?
        </p>

        <p className="text-[#0d0d0d]/50 text-center text-base font-medium font-['Uncut Sans Variable'] leading-tight">
          Kindly select how you would like top make this payment
        </p>

        <div className="border border-[#ececec] w-full sm:w-[80%] xl:w-[50%] p-4 rounded-[18px] mt-6">
          <div className="mt-2 flex justify-center w-full">
            <NotificationSwitch
              firstSwitch="Vendor"
              secondSwitch="Employee"
              setFilter={handleSwitch}
              fullWidth={true}
              activeBgColors="bg-[#5f38fa]"
              activeTextColor="text-white"
            />
          </div>
          <div className="flex flex-row gap-4 mt-4 w-full">
            {bulkPayeeFile.length === 0 && (
              <InitiateCard
                label="Manual entry"
                handleClick={handleManualEntryNavigation}
                // handleClick={() => navigate("/bulk-payout-manual-entry")}
              />
            )}
            <InitiateCard
              label="Bulk upload"
              downloadTemplate
              handleClick={() => handleUploadSupportingDocument()}
              handleDownload={downloadTemplate}
              bulkPayeeFile={bulkPayeeFile}
              setBulkPayeeFile={setBulkPayeeFile}
            />
          </div>
        </div>
      </div>
    </BulkPayoutLayout>
  );
};

export default InitiateBulkPayout;
